import React, { useState, useEffect } from 'react';
import './BookTable.css';

import { Heading } from '../../../components';
import { FaUser, FaEnvelope, FaPhone, FaUsers, FaComment, FaClock } from 'react-icons/fa';

import { FaCalendarDays } from 'react-icons/fa6';

import axios from "axios";

// API endpoint for sending reservation form data
const API_PATH = 'https://classicfeast-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regular expressions for validating email and phone number formats
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const BookTable = () => {

  // Form field state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    numberOfPeople: '',
    reservationDate: '',
    reservationTime: '',
    specialRequests: ''
  });

  // UI and control states
  const [mailSent, setMailSent] = useState(false);    // Tracks successful submission
  const [error, setError] = useState('');              // Tracks validation or submission errors
  const [isSubmitting, setIsSubmitting] = useState(false); // Controls submit button state

  // Auto-dismiss success alert after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      // Validate individual fields
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.numberOfPeople) validationErrors.push('Number of people is required');
      if (!formData.reservationDate) validationErrors.push('Reservation date is required');
      if (!formData.reservationTime) validationErrors.push('Reservation time is required');
      if (!formData.specialRequests) validationErrors.push('Special requests are required');

      // If there are validation issues, throw error to display them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data to send via PHP backend
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send the form data via Axios to the PHP backend
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle failed email sending
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        numberOfPeople: '',
        reservationDate: '',
        reservationTime: '',
        specialRequests: ''
      });
    } catch (err) {
      // Set error message from server or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form book-table linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Book Table'} subTitle={'Reserve Your Table'} sideHeading linear />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Your Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Number of People Input Field */}
              <div className="input-field">
                <label htmlFor="numberOfPeople"><FaUsers /></label>
                <input
                  type="number"
                  name="numberOfPeople"
                  className="box"
                  id="numberOfPeople"
                  placeholder="Number of People"
                  value={formData.numberOfPeople}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Reservation Date Input Field */}
              <div className="input-field"> 
                <label htmlFor="reservationDate"><FaCalendarDays /></label>
                <input
                  type="text"
                  name="reservationDate"
                  placeholder="Reservation Date"
                  id="reservationDate"
                  className="box"
                  value={formData.reservationDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Reservation Time Input Field */}
              <div className="input-field">
                <label htmlFor="reservationTime"><FaClock /></label>
                <input
                  type="text"
                  name="reservationTime"
                  placeholder="Reservation Time"
                  id="reservationTime"
                  className="box"
                  value={formData.reservationTime}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Special Requests Text Area */}
            <div className="input-field text-area">
              <label htmlFor="specialRequests"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="specialRequests"
                className="box"
                id="specialRequests"
                placeholder="Special Requests"
                value={formData.specialRequests}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}

            {/* Display success message */}
            {mailSent && <div className="alert success"><span>Table Reservation Submitted Successfully</span></div>}

          </form>
        </div>
      </div>
    </section>
  );
}

export default BookTable;